import { useEffect, useRef, useState } from 'react'
import type { SearchResult } from '../api/types.ts'
import { useChats } from '../store/chats.ts'

/**
 * ПОИСК ВНУТРИ ПЕРЕПИСКИ
 *
 * Не список найденного отдельной страницей, а перебор по ленте: нашёл
 * — и сразу видишь сообщение на своём месте, с тем, что было до и
 * после. Именно за этим в переписке и ищут: не «где слово», а «о чём
 * мы тогда договорились».
 *
 * Поэтому стрелки перебирают находки от свежих к старым, а счётчик
 * показывает, где ты в этой стопке. Без счётчика человек не понимает,
 * кончились находки или он просто топчется на одной.
 */
export function ChatSearch({
  chatId,
  members,
  onJump,
  onClose,
}: {
  chatId: string
  /** Кто в переписке — из них выбирают «от кого искать» */
  members: { id: string; displayName: string }[]
  onJump: (messageId: string) => void
  onClose: () => void
}) {
  const search = useChats((s) => s.search)
  const messageAtDate = useChats((s) => s.messageAtDate)
  const [query, setQuery] = useState('')
  const [from, setFrom] = useState<string>('')
  const [date, setDate] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [at, setAt] = useState(0)
  const [busy, setBusy] = useState(false)
  const [searched, setSearched] = useState(false)
  const [nothingAtDate, setNothingAtDate] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const run = useRef(0)

  /**
   * Переход держим в ссылке, а не в зависимостях.
   *
   * Родитель отдаёт сюда новую стрелочную функцию на каждую отрисовку.
   * Поставь её в зависимости поиска — и поиск будет перезапускаться
   * сам от себя без остановки.
   */
  const jump = useRef(onJump)
  jump.current = onJump

  useEffect(() => {
    input.current?.focus()
  }, [])

  // Ищем с задержкой: иначе запрос уходит на каждую букву,
  // а ответы возвращаются вперемешку
  useEffect(() => {
    const text = query.trim()

    /**
     * Со словами можно и не искать.
     *
     * «Покажи всё, что писала мама» — такой же осмысленный запрос.
     * Поэтому коротких слов ждём только тогда, когда никого не выбрали.
     */
    if (text.length < 2 && !from) {
      setResults([])
      setSearched(false)
      return
    }

    const mine = ++run.current
    setBusy(true)
    const timer = window.setTimeout(() => {
      search(text, chatId, from || undefined)
        .then((found) => {
          if (mine !== run.current) return
          setResults(found)
          setAt(0)
          setSearched(true)
          // Первую находку показываем сразу: человек ищет, чтобы увидеть,
          // а не чтобы узнать, сколько раз он это писал
          if (found[0]) jump.current(found[0].id)
        })
        .catch(() => {
          if (mine === run.current) setResults([])
        })
        .finally(() => {
          if (mine === run.current) setBusy(false)
        })
    }, 350)

    return () => window.clearTimeout(timer)
  }, [query, from, chatId, search])

  /**
   * Шаг по находкам.
   *
   * Сервер отдаёт их от свежих к старым, а стрелки должны вести туда
   * же, куда ведёт лента: «вверх» — в прошлое, «вниз» — к сегодняшнему.
   * Поэтому «вверх» — это шаг вперёд по списку, а не назад.
   */
  const step = (toOlder: boolean) => {
    if (results.length === 0) return
    const delta = toOlder ? 1 : -1
    const next = (at + delta + results.length) % results.length
    setAt(next)
    const found = results[next]
    if (found) jump.current(found.id)
  }

  const bar = (
    <div className="chat-search">
      <input
        ref={input}
        className="chat-search-input"
        placeholder="Поиск в переписке"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose()
          // Enter ведёт к следующей находке, Shift+Enter — к предыдущей:
          // так же, как поиск по странице в браузере
          if (event.key === 'Enter') {
            event.preventDefault()
            step(!event.shiftKey)
          }
        }}
      />

      <span className="chat-search-count">
        {busy
          ? '…'
          : results.length > 0
            ? `${at + 1} из ${results.length}`
            : searched
              ? 'Ничего'
              : ''}
      </span>

      <button
        className="icon-btn"
        onClick={() => step(true)}
        disabled={results.length === 0}
        aria-label="Раньше"
        title="Раньше (Enter)"
      >
        ↑
      </button>
      <button
        className="icon-btn"
        onClick={() => step(false)}
        disabled={results.length === 0}
        aria-label="Позже"
        title="Позже (Shift+Enter)"
      >
        ↓
      </button>
      <button className="icon-btn" onClick={onClose} aria-label="Закрыть поиск">
        ✕
      </button>
    </div>
  )

  return (
    <div className="chat-search-wrap">
      {bar}

      <div className="chat-search-filters">
        {/* «От кого» имеет смысл только там, где собеседников несколько */}
        {members.length > 2 && (
          <select
            className="chat-search-from"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            aria-label="От кого"
          >
            <option value="">От кого угодно</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName}
              </option>
            ))}
          </select>
        )}

        {/**
         * Переход к дате.
         *
         * «А что мы решили в мае?» — листать до мая по сообщению за раз
         * невозможно. Выбранный день открывается сразу.
         */}
        <input
          className="chat-search-date"
          type="date"
          value={date}
          onChange={(event) => {
            const picked = event.target.value
            setDate(picked)
            if (!picked) return
            messageAtDate(chatId, picked)
              .then((id) => jump.current(id))
              .catch(() => setNothingAtDate(true))
          }}
          aria-label="Перейти к дате"
        />

        {nothingAtDate && <span className="chat-search-note">В тот день и позже не писали</span>}
      </div>
    </div>
  )
}
